"use client";

import { Badge } from "@universe/ui/components/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
} from "@universe/ui/components/sidebar";
import { useTranslations } from "next-intl";
import * as React from "react";

import { AppLink } from "@/components/app-link";
import { NavMain } from "@/components/nav-main";
import { NavSupport } from "@/components/nav-support";
import { Route } from "@/enums/route";
import { useCustomerAccount, useWhitelabel } from "@/providers";

import CompanyLogo from "./company-logo";
import PoweredByUM from "./powered-by-um";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const isDemoEnvironment =
    process.env.NEXT_PUBLIC_BACKEND_ENVIRONMENT !== "prd";
  const whitelabel = useWhitelabel();
  const { isAccountSuspended } = useCustomerAccount();
  const tNav = useTranslations("nav");

  return (
    <Sidebar variant="floating" {...props} collapsible="offcanvas">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <AppLink
              href={Route.Dashboard}
              className="flex flex-col items-center gap-2 max-w-[280px]"
            >
              <CompanyLogo
                width={190}
                height={190}
                className="py-1 fade-in duration-300"
                variant={
                  whitelabel.isWhitelabel
                    ? (whitelabel.sidebarLogoVariant ?? "default")
                    : "light"
                }
              />
              {/* Suspended (view-only) takes the badge slot over the demo
                  tag. Same frosted treatment as the Demo account badge so it
                  picks up the whitelabel sidebar colour through
                  transparency. */}
              {isAccountSuspended ? (
                <Badge
                  variant="secondary"
                  data-testid="suspended-view-only-pill"
                  className="bg-white/20 text-white border-white/30 hover:bg-white/25 text-sm"
                >
                  {tNav("viewOnly")}
                </Badge>
              ) : (
                isDemoEnvironment && (
                  <Badge
                    variant="secondary"
                    className="bg-white/20 text-white border-white/30 hover:bg-white/25 text-sm"
                  >
                    Demo account
                  </Badge>
                )
              )}
            </AppLink>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain />
      </SidebarContent>
      <SidebarFooter>
        {whitelabel.isWhitelabel && <PoweredByUM />}
        <NavSupport />
      </SidebarFooter>
    </Sidebar>
  );
}
